import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useState,
} from 'react'
import { useBoolean, useSetState } from 'react-use'

import { IPluginPresenter } from '@makyoapp/makyo-sdk/models/Plugins'
import { DbSource, TeamAdapter } from '@makyoapp/makyo-common'

export interface DbTestState {
  ping: number
  createTable: number
  addRow: number
  getRow: number
  updateRow: number
  delRow: number
  delTable: number
  error?: string
  ended: boolean
}

export enum DbTestType {
  Ping = 'ping',
  CreateTable = 'create-table',
  AddRow = 'add-row',
  GetRow = 'get-row',
  UpdateRow = 'update-row',
  DeleteRow = 'delete-row',
  DeleteTable = 'delete-table',
  Reset = 'reset',
}

export enum DbTestFlag {
  NotStarted = 0,
  Loading = 1,
  Success = 2,
  Failed = 3,
}

export interface DbTestAction {
  type: DbTestType
  payload: DbTestFlag
  error?: string
}

export const DB_TEST_DEFAULT: DbTestState = {
  ping: DbTestFlag.NotStarted,
  createTable: DbTestFlag.NotStarted,
  addRow: DbTestFlag.NotStarted,
  getRow: DbTestFlag.NotStarted,
  updateRow: DbTestFlag.NotStarted,
  delRow: DbTestFlag.NotStarted,
  delTable: DbTestFlag.NotStarted,
  error: undefined,
  ended: false,
}

export interface DbPluginListItem {
  id: string
  name: string
  type: 'db' | 'api'
  icon: string | React.ReactNode
  data?: IPluginPresenter
}

interface CreateDbValue {
  source?: DbSource
  sourceAlt?: DbPluginListItem
  db: string
  pluginRefId?: string
  sampleTables: boolean
  checkAllTables: boolean
  otherTables: string[]
  teams: TeamAdapter[]
  dbSettings: Record<string, string>
  aiType?: string
}

const initialCreateDbValue: CreateDbValue = {
  db: '',
  source: undefined,
  sourceAlt: undefined,
  sampleTables: true,
  checkAllTables: false,
  otherTables: [],
  teams: [],
  dbSettings: {},
  aiType: undefined,
}

export interface CreateDbContextState {
  /**
   * the input values
   */
  value: CreateDbValue
  /**
   * set the input values
   * @param nextValue
   */
  setValue: (nextValue: Partial<CreateDbValue>) => void
  /**
   * forced checking teams, it will disabled checking to force the team to always be selected
   */
  forcedTeams: TeamAdapter[]
  setForcedTeams: Dispatch<SetStateAction<TeamAdapter[]>>
  /**
   * wizard step
   */
  step: number
  /**
   * set step value
   * @param nextStep
   */
  setStep: (nextStep: number) => void
  /**
   * search keyword, for searching 3rd-party tables/databases
   */
  search: string
  /**
   * set search keyword state
   * @param nextSearch
   */
  setSearch: (nextSearch: string) => void
  reset: () => void

  showModal: boolean
  setShowModal: (next: boolean) => void

  onAfterUpdate?: () => void
  testState: DbTestState
  testDispatch: Dispatch<DbTestAction>
  testStarted: boolean
  setTestStarted: (next: boolean) => void
  testEnded: boolean
  setTestEnded: (next: boolean) => void
  error: string
  setError: (next: string) => void
  requirements?: Record<string, unknown>
  setRequirements: Dispatch<SetStateAction<Record<string, unknown> | undefined>>
  extTables: string[]
  setExtTables: Dispatch<SetStateAction<string[]>>
  importType: 'csv' | 'excel' | ''
  setImportType: Dispatch<SetStateAction<'csv' | 'excel' | ''>>
  isLoading: boolean
  setIsLoading: Dispatch<SetStateAction<boolean>>
  backButtonText: string
}

const CreateDbContext = createContext<CreateDbContextState>({
  value: initialCreateDbValue,
  setValue: () => null,
  forcedTeams: [],
  setForcedTeams: () => null,
  step: 1,
  setStep: () => null,
  search: '',
  setSearch: () => null,
  reset: () => null,
  showModal: false,
  setShowModal: () => null,
  testState: DB_TEST_DEFAULT,
  testDispatch: () => null,
  testStarted: false,
  setTestStarted: () => null,
  testEnded: false,
  setTestEnded: () => null,
  error: '',
  setError: () => null,
  requirements: undefined,
  setRequirements: () => null,
  extTables: [],
  setExtTables: () => null,
  importType: '',
  setImportType: () => null,
  isLoading: false,
  setIsLoading: () => null,
  backButtonText: 'Back',
})

/**
 * use create db context hook
 */
export function useCreateDbContext() {
  return useContext(CreateDbContext)
}

/**
 * CreateDbContext provider
 */
export const CreateDbProvider = ({
  children,
  backButtonText = 'Back',
}: {
  children?: React.ReactNode
  backButtonText?: string
}) => {
  const [value, setValue] = useSetState<CreateDbValue>(initialCreateDbValue)
  const [step, setStep] = useState(1)
  const [importType, setImportType] = useState<'csv' | 'excel' | ''>('')
  const [isLoading, setIsLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [showModal, setShowModal] = useBoolean(false)
  const [testStarted, setTestStarted] = useBoolean(false)
  const [testEnded, setTestEnded] = useBoolean(false)
  const [error, setError] = useState('')
  const [extTables, setExtTables] = useState<string[]>([])
  const [requirements, setRequirements] = useState<Record<string, unknown> | undefined>(undefined)

  const [forcedTeams, setForcedTeams] = useState<TeamAdapter[]>([])

  useEffect(() => {
    if (forcedTeams.length > 0) {
      const newTeams = [...value.teams]
      let hasChange = false
      for (const ft of forcedTeams) {
        const isExist = newTeams.find((nt) => nt.id === ft.id)
        if (!isExist) {
          hasChange = true
          newTeams.push(ft)
        }
      }

      if (hasChange) {
        setValue({
          ...value,
          teams: newTeams,
        })
      }
    }
  }, [forcedTeams, value, setValue])

  function reducer(state: DbTestState, action: DbTestAction) {
    switch (action.type) {
      case DbTestType.Ping:
        return {
          ...state,
          ping: action.payload,
          error: action.error,
        }
      case DbTestType.CreateTable:
        return {
          ...state,
          createTable: action.payload,
          error: action.error,
        }
      case DbTestType.AddRow:
        return {
          ...state,
          addRow: action.payload,
          error: action.error,
        }
      case DbTestType.GetRow:
        return {
          ...state,
          getRow: action.payload,
          error: action.error,
        }
      case DbTestType.UpdateRow:
        return {
          ...state,
          updateRow: action.payload,
          error: action.error,
        }
      case DbTestType.DeleteRow:
        return {
          ...state,
          delRow: action.payload,
          error: action.error,
        }
      case DbTestType.DeleteTable:
        return {
          ...state,
          delTable: action.payload,
          error: action.error,
          ended: true,
        }
      case DbTestType.Reset:
        return {
          ...DB_TEST_DEFAULT,
        }
      default:
        return state
    }
  }

  const [testState, testDispatch] = useReducer(reducer, DB_TEST_DEFAULT)

  const reset = useCallback(() => {
    setStep(1)
    setValue(initialCreateDbValue)
    setSearch('')
    setTestStarted(false)
    testDispatch({
      type: DbTestType.Reset,
      payload: 0,
    })
    setError('')
    setRequirements(undefined)
  }, [setStep, setValue, setSearch, testDispatch, setTestStarted, setError, setRequirements])

  return (
    <>
      <CreateDbContext.Provider
        value={{
          value,
          setValue,
          forcedTeams,
          setForcedTeams,
          step,
          setStep,
          search,
          setSearch,
          reset,
          showModal,
          setShowModal,
          testState,
          testDispatch,
          testStarted,
          setTestStarted,
          testEnded,
          setTestEnded,
          error,
          setError,
          requirements,
          setRequirements,
          extTables,
          setExtTables,
          importType,
          setImportType,
          isLoading,
          setIsLoading,
          backButtonText: step > 1 ? 'Back' : backButtonText,
        }}
      >
        {children}
      </CreateDbContext.Provider>
    </>
  )
}
